import { useState, useEffect } from 'react';
import {
    MenuContainer,
    SearchInputContainer,
    AppliedTagsContainer,
    TagsListContainer,
    TagRow,
} from './TagEditMenu.styles';
import PargodaTag from '../PargodaTag';
import {
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Popover,
    Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ITag } from '../../../../common';
import { useTranslation } from 'react-i18next';
import Icon from '../../Icon';
import { SketchPicker } from 'react-color';
import { organizationSelectors } from '../../../../actions';
import { useAppSelector } from '../../../base-redux/hooks';

interface TagEditMenuProps {
    isFilter?: boolean;
    appliedTags: Array<ITag>;
    existingTags: Array<ITag>;
    onTagCreate: (tagName: string) => void;
    onTagRemove: (tagId: number) => void;
    onTagUpdate: (tag: ITag) => void;
    onTagApply: (tag: ITag) => void;
    onTagUnapply: (tagId: number) => void;
}

const TagEditMenu: React.FC<TagEditMenuProps> = ({
    isFilter = false,
    appliedTags,
    existingTags,
    onTagCreate,
    onTagRemove,
    onTagUpdate: tagUpdate,
    onTagApply,
    onTagUnapply,
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedTag, setSelectedTag] = useState<null | ITag>(null);
    const [editingTag, setEditingTag] = useState<ITag | null>(null);
    const [editedTagName, setEditedTagName] = useState<string>('');
    const [colorPickerAnchorEl, setColorPickerAnchorEl] =
        useState<null | HTMLElement>(null);
    const [selectedColor, setSelectedColor] = useState<string | undefined>(
        undefined
    );
    const theme = useTheme();
    const isTagUpdating = useAppSelector(
        organizationSelectors.isUpdateTagLoading
    );

    useEffect(() => {
        if (!isTagUpdating) {
            setEditingTag(null);
        }
    }, [isTagUpdating]);

    const handleCreateTag = () => {
        if (
            searchTerm.trim() !== '' &&
            !existingTags.some((tag) => tag.label === searchTerm.trim())
        ) {
            onTagCreate(searchTerm.trim());
            setSearchTerm('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleCreateTag();
        }
    };

    const handleMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        tag: ITag
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedTag(tag);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleColorPickerOpen = () => {
        setColorPickerAnchorEl(anchorEl);
        handleMenuClose();
    };

    const handleColorPickerClose = () => {
        setColorPickerAnchorEl(null);
        setSelectedColor(undefined);
    };

    const handleColorChange = (color: { hex: string }) => {
        setSelectedColor(color.hex);
    };

    const handleColorSave = () => {
        if (selectedTag && selectedColor) {
            tagUpdate({
                ...selectedTag,
                color: selectedColor,
            });
            setEditingTag(null);
        }
        handleColorPickerClose();
    };

    const handleStartEditing = (tag: ITag) => {
        setEditingTag(tag);
        setEditedTagName(tag.label);
        handleMenuClose();
    };

    const handleFinishEditing = () => {
        if (editingTag && editedTagName.trim() !== '') {
            tagUpdate({ ...editingTag, label: editedTagName.trim() });
        }
        setEditingTag(null);
    };

    const filteredTags = existingTags.filter(
        (tag) =>
            !appliedTags.some((applied) => applied.tag_id === tag.tag_id) &&
            tag.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <MenuContainer>
            <AppliedTagsContainer>
                {appliedTags.map((tag) => (
                    <PargodaTag
                        key={tag.tag_id}
                        tag={tag}
                        onRemove={() =>
                            onTagUnapply(isFilter ? tag.tag_id : tag.id)
                        }
                    />
                ))}
            </AppliedTagsContainer>
            <SearchInputContainer>
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t('tagsEditor.searchOrCreateNew')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    sx={{
                                        cursor: 'pointer',
                                        height: 24,
                                        width: 24,
                                        marginInlineEnd: theme.spacing(1),
                                    }}
                                    onClick={handleCreateTag}
                                >
                                    <Icon name="CheckRoundedIcon" size={20} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </SearchInputContainer>
            <TagsListContainer>
                {filteredTags.map((tag) =>
                    editingTag?.tag_id === tag.tag_id ? (
                        <TagRow key={tag.tag_id}>
                            <TextField
                                fullWidth
                                autoFocus
                                variant="outlined"
                                size="small"
                                value={editedTagName}
                                onChange={(e) =>
                                    setEditedTagName(e.target.value)
                                }
                                onBlur={(e) => {
                                    e.stopPropagation();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleFinishEditing();
                                    }
                                    if (e.key === 'Escape') {
                                        setEditingTag(null);
                                    }
                                }}
                                disabled={isTagUpdating}
                                InputProps={{
                                    endAdornment: isTagUpdating ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleFinishEditing}
                                                edge="end"
                                            >
                                                <Icon
                                                    name="CheckRoundedIcon"
                                                    size={20}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </TagRow>
                    ) : (
                        <TagRow
                            key={tag.tag_id}
                            onClick={() => onTagApply(tag)}
                        >
                            <PargodaTag tag={tag} />
                            <IconButton
                                sx={{
                                    cursor: 'pointer',
                                    height: 24,
                                    width: 24,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMenuOpen(e, tag);
                                }}
                            >
                                {!isFilter && (
                                    <Icon name="MoreVertIcon" size={20} />
                                )}
                            </IconButton>
                        </TagRow>
                    )
                )}
            </TagsListContainer>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem
                    onClick={() => {
                        if (selectedTag) {
                            handleStartEditing(selectedTag);
                        }
                    }}
                >
                    <Icon name="EditIcon" style={{ marginInlineEnd: '8px' }} />
                    {t('tagsEditor.rename')}
                </MenuItem>
                <MenuItem onClick={handleColorPickerOpen}>
                    <Icon
                        name="ColorLensIcon"
                        sx={{ marginInlineEnd: '8px' }}
                    />
                    {t('tagsEditor.changeColor')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (selectedTag) {
                            onTagRemove(selectedTag.tag_id || null);
                        }
                        handleMenuClose();
                    }}
                    style={{ color: 'red' }}
                >
                    <Icon
                        name="DeleteIcon"
                        sx={{ marginInlineEnd: '8px', height: 18, width: 18 }}
                    />
                    {t('tagsEditor.delete')}
                </MenuItem>
            </Menu>
            <Popover
                open={Boolean(colorPickerAnchorEl)}
                anchorEl={colorPickerAnchorEl}
                onClose={handleColorPickerClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <SketchPicker
                    color={selectedColor || selectedTag?.color}
                    onChangeComplete={handleColorChange}
                    styles={{
                        default: {
                            picker: {
                                border: 'none',
                                boxShadow: 'none',
                            },
                        },
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        p: 1,
                        paddingInlineEnd: 2,
                    }}
                >
                    <IconButton
                        onClick={handleColorPickerClose}
                        sx={{ marginInlineEnd: 1, height: 18, width: 18 }}
                        size="small"
                    >
                        <Icon name="CloseIcon" size={16} />
                    </IconButton>
                    <IconButton
                        onClick={handleColorSave}
                        color="primary"
                        size="small"
                        sx={{ height: '18px', width: '18px' }}
                    >
                        <Icon name="CheckRoundedIcon" size={16} />
                    </IconButton>
                </Box>
            </Popover>
        </MenuContainer>
    );
};

export default TagEditMenu;
